"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  LightbulbIcon,
  ThumbsUpIcon,
  MessageSquareIcon,
  StarIcon,
  MessageSquare,
  ArrowUpFromLine,
  ThumbsUp,
  Filter
} from "lucide-react";
import Link from "next/link";
import { toast } from "sonner";
import {
  AnimatedSection,
  AnimatedFeature,
  AnimatedButton,
  AnimatedIcon,
  staggerContainer
} from "../../components/AnimatedComponents";

interface FeatureRequest {
  id: number;
  title: string;
  description: string;
  category: string;
  status: "pending" | "planned" | "in-progress" | "completed" | "rejected";
  votes: number;
  comments: number;
  createdAt: string;
}

const mockFeatureRequests: FeatureRequest[] = [
  {
    id: 1,
    title: "Property comparison tool",
    description: "Allow users to compare multiple properties side by side with key metrics",
    category: "Search & Discovery",
    status: "planned",
    votes: 124,
    comments: 18,
    createdAt: "2025-03-15"
  },
  {
    id: 2,
    title: "Neighborhood safety ratings",
    description: "Add safety scores based on crime data for neighborhoods",
    category: "Property Info",
    status: "in-progress",
    votes: 98,
    comments: 12,
    createdAt: "2025-04-01"
  },
  {
    id: 3,
    title: "Virtual tours integration",
    description: "Add support for 3D virtual tours of properties",
    category: "Viewing",
    status: "completed",
    votes: 156,
    comments: 24,
    createdAt: "2025-02-20"
  },
  {
    id: 4,
    title: "Rent price history graph",
    description: "Show historical rent prices for properties over time",
    category: "Property Info",
    status: "pending",
    votes: 87,
    comments: 9,
    createdAt: "2025-04-10"
  },
  {
    id: 5,
    title: "Landlord response time metric",
    description: "Add average response time for landlords based on tenant feedback",
    category: "Landlord Metrics",
    status: "planned",
    votes: 76,
    comments: 15,
    createdAt: "2025-03-28"
  },
  {
    id: 6,
    title: "Mobile app for RentWise",
    description: "Create a native mobile app for iOS and Android",
    category: "Platform",
    status: "in-progress",
    votes: 210,
    comments: 31,
    createdAt: "2025-01-05"
  }
];

const categoryColors: Record<string, string> = {
  "Search & Discovery": "bg-purple-100 text-purple-600",
  "Property Info": "bg-blue-100 text-blue-600",
  "Viewing": "bg-green-100 text-green-700",
  "Landlord Metrics": "bg-amber-100 text-amber-700",
  "Platform": "bg-rose-100 text-rose-700",
  "Tenant Tools": "bg-orange-100 text-orange-700"
};

const statusColors: Record<string, string> = {
  "pending": "bg-gray-100 text-gray-700",
  "planned": "bg-blue-100 text-blue-700",
  "in-progress": "bg-purple-100 text-purple-700",
  "completed": "bg-green-100 text-green-700",
  "rejected": "bg-red-100 text-red-700"
};

const statusEmojis: Record<string, string> = {
  "pending": "🔍",
  "planned": "📋",
  "in-progress": "⚙️",
  "completed": "✅",
  "rejected": "❌"
};

export default function FeatureRequestsPage() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Search & Discovery");
  const [activeTab, setActiveTab] = useState<"all" | "popular" | "recent" | "completed">("all");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isLoggedIn) {
      toast.error("Authentication required", {
        description: "Please sign in to submit a feature request",
      });
      return;
    }
    
    if (title.trim() === "" || description.trim() === "") {
      toast.error("Missing information", {
        description: "Please fill out all required fields",
      });
      return;
    }
    
    // In a real app, this would be an API call
    toast.success("Feature request submitted", {
      description: "Thank you for your contribution!",
    });
    
    // Reset form
    setTitle("");
    setDescription("");
    setCategory("Search & Discovery");
  };

  const filteredRequests = mockFeatureRequests.filter((request) => {
    if (activeTab === "all") return true;
    if (activeTab === "popular") return request.votes > 100;
    if (activeTab === "recent") {
      const date = new Date(request.createdAt);
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      return date > oneMonthAgo;
    }
    if (activeTab === "completed") return request.status === "completed";
    return true;
  });

  const upvoteFeature = (id: number) => {
    if (!isLoggedIn) {
      toast.error("Authentication required", {
        description: "Please sign in to vote on feature requests",
      });
      return;
    }
    
    toast.success("Vote recorded", {
      description: "Thank you for your feedback!",
    });
  };

  return (
    <main className="min-h-screen bg-linear-to-b from-purple-50 to-white">
      {/* Hero section */}
      <AnimatedSection initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="bg-linear-to-r from-purple-400 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="flex flex-col items-center text-center">
            <div className="p-4 bg-white bg-opacity-20 rounded-full mb-6">
              <LightbulbIcon size={40} className="text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-linear-to-r from-white to-purple-100">Feature Requests</h1>
            <p className="text-lg md:text-xl max-w-3xl mb-8">
              Help shape the future of RentWise by sharing your ideas and voting on features
              that matter most to you. Together, we can build a better platform for everyone.
            </p>
          </div>
        </div>
      </AnimatedSection>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Submit feature request */}
          <AnimatedSection initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-1">
            <div className="glass-card p-6 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold mb-4 bg-clip-text text-transparent bg-linear-to-r from-purple-400 to-purple-600">Submit Your Idea</h2>
              <p className="text-gray-600 mb-6">
                Have a great idea for RentWise? We'd love to hear it! Fill out the form below to submit your feature request.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Feature Title*
                  </label>
                  <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                    placeholder="A brief title for your feature idea"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                  >
                    <option value="Search & Discovery">Search & Discovery</option>
                    <option value="Property Info">Property Info</option>
                    <option value="Viewing">Viewing</option>
                    <option value="Landlord Metrics">Landlord Metrics</option>
                    <option value="Platform">Platform</option>
                    <option value="Tenant Tools">Tenant Tools</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description*
                  </label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="min-h-[120px]"
                    placeholder="Describe your feature idea in detail. What problem does it solve? Who would benefit from it?"
                    required
                  />
                </div>
                <AnimatedButton floating>
                  <Button 
                    type="submit" 
                    className="w-full bg-linear-to-r from-purple-400 to-purple-600 hover:opacity-90 text-lg py-3 rounded-lg shadow-md"
                  >
                    Submit Feature Request
                  </Button>
                </AnimatedButton>
                {!isLoggedIn && (
                  <p className="text-xs text-gray-500 text-center mt-2">
                    You'll need to <Link href="/login" className="text-purple-600 hover:underline">sign in</Link> to submit a feature request
                  </p>
                )}
              </form>
            </div>
            <div className="glass-card p-6 rounded-xl shadow-md mt-8">
              <h3 className="text-xl font-bold mb-4 bg-clip-text text-transparent bg-linear-to-r from-purple-400 to-purple-600">How It Works</h3>
              <ol className="space-y-4">
                {["Submit your feature request using the form","Our team reviews all submissions","Approved ideas get published for community voting","Popular features get prioritized for development","We'll keep you updated on the progress of your ideas"].map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <AnimatedIcon index={idx} className="shrink-0 h-6 w-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </AnimatedIcon>
                    <p className="text-gray-700">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          </AnimatedSection>
          {/* Feature requests list */}
          <AnimatedSection variants={staggerContainer} className="lg:col-span-2">
            <div className="glass-card p-6 rounded-xl shadow-md">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-linear-to-r from-purple-400 to-purple-600">Community Requests</h2>
                <div className="flex flex-wrap gap-2">
                  {(["all","popular","recent","completed"] as const).map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${activeTab === tab 
                        ? "bg-purple-400 text-white" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {filteredRequests.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No feature requests found.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredRequests.map((request, idx) => (
                    <AnimatedFeature key={request.id} index={idx} className="glass-card border border-gray-200 rounded-lg p-4 hover:border-purple-400 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-2 ${categoryColors[request.category]}`}>
                            {request.category}
                          </span>
                          <h3 className="text-lg font-semibold">{request.title}</h3>
                          <p className="text-gray-600 mt-1">{request.description}</p>
                        </div>
                        <AnimatedButton>
                          <button
                            onClick={() => upvoteFeature(request.id)}
                            className="flex flex-col items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg p-2 transition-colors"
                          >
                            <ThumbsUpIcon size={16} className="text-gray-700" />
                            <span className="text-sm font-medium text-gray-700 mt-1">{request.votes}</span>
                          </button>
                        </AnimatedButton>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusColors[request.status]}`}>
                            <span>{statusEmojis[request.status]}</span>
                            <span className="capitalize">{request.status}</span>
                          </span>
                          <span className="text-xs text-gray-500">
                            Created: {request.createdAt}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <MessageSquareIcon size={14} />
                            {request.comments}
                          </span>
                        </div>
                      </div>
                    </AnimatedFeature>
                  ))}
                </div>
              )}
            </div>
            
            <div className="glass-card p-6 rounded-xl shadow-md mt-8">
              <h3 className="text-xl font-bold mb-4">Feature Roadmap</h3>
              <p className="text-gray-600 mb-6">
                Here's what we're currently working on and planning for future releases.
                The roadmap is subject to change based on community feedback and priorities.
              </p>
              
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-2">Legend</h4>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                    <span>⚙️</span>
                    <span>In Progress</span>
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                    <span>📋</span>
                    <span>Planned</span>
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                    <span>🔍</span>
                    <span>Under Review</span>
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                    <span>✅</span>
                    <span>Completed</span>
                  </span>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Target Release</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Advanced Property Search Filters</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                        <span>⚙️</span>
                        <span>In Progress</span>
                      </span>
                    </TableCell>
                    <TableCell>Q2 2025</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Landlord Verification System</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                        <span>✅</span>
                        <span>Completed</span>
                      </span>
                    </TableCell>
                    <TableCell>Released</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Rental Contract Generator</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                        <span>📋</span>
                        <span>Planned</span>
                      </span>
                    </TableCell>
                    <TableCell>Q3 2025</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Tenant Credit Score Integration</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                        <span>🔍</span>
                        <span>Under Review</span>
                      </span>
                    </TableCell>
                    <TableCell>TBD</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            
            <div className="glass-card bg-linear-to-r from-purple-400 to-purple-600 p-6 rounded-xl shadow-md mt-8 text-white">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white rounded-full bg-opacity-20">
                  <StarIcon size={24} className="text-white" />
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-2">Join Our Beta Program</h3>
                  <p className="mb-4">
                    Get early access to new features and help us improve them before they're released to everyone.
                  </p>
                  <Button variant="secondary" className="bg-white text-purple-600 hover:bg-gray-100">
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </main>
  );
} 